import { z } from 'zod';
import { uuidSchema, LoginTypeSchema, cognitoPasswordSchema } from './common.schema.js';
import { UserRoleSchema } from './user.schema.js';
import { IdpTypeEnum } from './tenant.schema.js';

/**
 * Authentication & Authorization Schemas
 */

export const LoginRequestSchema = z.object({
  email: z.string().email('Invalid email format'),
  password: z.string().min(1, 'Password is required'),
  loginType: LoginTypeSchema,
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

/** Legacy token shape used by tms-api-service */
export const LoginResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  expiresIn: z.number(),
  tokenType: z.literal('Bearer'),
});
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

export const CognitoLoginUserSchema = z.object({
  username: z.string(),
  tenant_id: uuidSchema.optional(),
  hospital_id: uuidSchema.optional(),
  roles: z.string().optional(),
  default_language: z.string().optional(),
  person_id: uuidSchema.optional(),
  time_zone: z.string().optional(),
});
export type CognitoLoginUser = z.infer<typeof CognitoLoginUserSchema>;

export const CognitoTokenResponseSchema = z.object({
  id_token: z.string(),
  access_token: z.string(),
  refresh_token: z.string().optional(),
  token_type: z.string(),
  expires_in: z.number(),
  user: CognitoLoginUserSchema,
});
export type CognitoTokenResponse = z.infer<typeof CognitoTokenResponseSchema>;

/**
 * Cognito may return a challenge instead of tokens
 * (e.g. NEW_PASSWORD_REQUIRED, SMS_MFA, SOFTWARE_TOKEN_MFA).
 */
export const LoginChallengeResponseSchema = z.object({
  challengeName: z.string(),
  session: z.string(),
  challengeParameters: z.record(z.string(), z.string()).optional(),
});
export type LoginChallengeResponse = z.infer<typeof LoginChallengeResponseSchema>;

/**
 * Union response: either tokens (success) or a challenge that the UI must handle.
 * mfaSetupRequired is set when the user has no configured MFA preference.
 * On the challenge branch it signals that the user should complete MFA setup before verifying.
 */
export const CognitoLoginResponseSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('authenticated'),
    tokens: CognitoTokenResponseSchema,
    mfaSetupRequired: z.boolean().optional(),
  }),
  z.object({
    type: z.literal('challenge'),
    challenge: LoginChallengeResponseSchema,
    mfaSetupRequired: z.boolean().optional(),
  }),
]);
export type CognitoLoginResponse = z.infer<typeof CognitoLoginResponseSchema>;

export const RefreshTokenRequestSchema = z.object({
  refreshToken: z.string(),
});
export type RefreshTokenRequest = z.infer<typeof RefreshTokenRequestSchema>;

/** Cognito-flavoured refresh-token request (snake_case, matching Cognito token field names). */
export const CognitoRefreshTokenRequestSchema = z.object({
  // PHX-1180: optional — the backend falls back to the httpOnly `tms_refresh_token` cookie
  // when the caller (post-reload SPA) no longer holds the token in memory.
  refresh_token: z.string().min(1, 'Refresh token is required').optional(),
  username: z.string().min(1, 'Username is required'),
  loginType: LoginTypeSchema,
});
export type CognitoRefreshTokenRequest = z.infer<typeof CognitoRefreshTokenRequestSchema>;

export const CognitoRefreshTokenResponseSchema = z.object({
  id_token: z.string(),
  access_token: z.string(),
  token_type: z.string(),
  expires_in: z.number(),
});
export type CognitoRefreshTokenResponse = z.infer<typeof CognitoRefreshTokenResponseSchema>;

export const LogoutRequestSchema = z.object({
  // PHX-1180: optional — the backend falls back to the httpOnly `tms_access_token`
  // cookie when the caller (post-reload SPA) no longer holds the token in memory.
  access_token: z.string().min(1, 'Access token is required').optional(),
});
export type LogoutRequest = z.infer<typeof LogoutRequestSchema>;

/**
 * Unified respond-to-challenge schema.
 * Handles NEW_PASSWORD_REQUIRED, SOFTWARE_TOKEN_MFA, EMAIL_OTP, and SELECT_MFA_TYPE.
 * username is USER_ID_FOR_SRP from challengeParameters — must NOT be forced to UUID.
 */
export const RespondToChallengeRequestSchema = z.discriminatedUnion('challengeName', [
  z.object({
    challengeName: z.literal('NEW_PASSWORD_REQUIRED'),
    session: z.string().min(1, 'session is required'),
    username: z.string().min(1, 'username is required'),
    newPassword: cognitoPasswordSchema,
    loginType: LoginTypeSchema,
  }),
  z.object({
    challengeName: z.literal('SOFTWARE_TOKEN_MFA'),
    session: z.string().min(1, 'session is required'),
    username: z.string().min(1, 'username is required'),
    code: z.string().min(1, 'code is required'),
    loginType: LoginTypeSchema,
  }),
  z.object({
    challengeName: z.literal('EMAIL_OTP'),
    session: z.string().min(1, 'session is required'),
    username: z.string().min(1, 'username is required'),
    code: z.string().min(1, 'code is required'),
    loginType: LoginTypeSchema,
  }),
  z.object({
    /** Returned when multiple MFA methods are active; answer selects which to use. */
    challengeName: z.literal('SELECT_MFA_TYPE'),
    session: z.string().min(1, 'session is required'),
    username: z.string().min(1, 'username is required'),
    answer: z.enum(['SOFTWARE_TOKEN_MFA', 'EMAIL_OTP']),
    loginType: LoginTypeSchema,
  }),
  z.object({
    /** Newer Cognito variant of SELECT_MFA_TYPE for pools with multiple MFA methods. */
    challengeName: z.literal('CONTINUE_SIGN_IN_WITH_MFA_SELECTION'),
    session: z.string().min(1, 'session is required'),
    username: z.string().min(1, 'username is required'),
    answer: z.enum(['SOFTWARE_TOKEN_MFA', 'EMAIL_OTP']),
    loginType: LoginTypeSchema,
  }),
  z.object({
    /** Finalizer after completing in-auth MFA setup (Required MFA pools). */
    challengeName: z.literal('MFA_SETUP'),
    session: z.string().min(1, 'session is required'),
    username: z.string().min(1, 'username is required'),
    loginType: LoginTypeSchema,
  }),
]);
export type RespondToChallengeRequest = z.infer<typeof RespondToChallengeRequestSchema>;

// ── In-auth MFA setup (Required MFA pools — session-based, no access token) ──

export const MfaSetupTotpInitiateRequestSchema = z.object({
  session: z.string().min(1, 'session is required'),
  username: z.string().min(1, 'username is required'),
  loginType: LoginTypeSchema,
});
export type MfaSetupTotpInitiateRequest = z.infer<typeof MfaSetupTotpInitiateRequestSchema>;

export const MfaSetupTotpInitiateResponseSchema = z.object({
  session: z.string(),
  secretCode: z.string(),
  otpauthUri: z.string(),
  qrCodeDataUrl: z.string(),
});
export type MfaSetupTotpInitiateResponse = z.infer<typeof MfaSetupTotpInitiateResponseSchema>;

export const MfaSetupTotpCompleteRequestSchema = z.object({
  session: z.string().min(1, 'session is required'),
  username: z.string().min(1, 'username is required'),
  code: z.string().regex(/^\d{6}$/, 'code must be exactly 6 digits'),
  loginType: LoginTypeSchema,
});
export type MfaSetupTotpCompleteRequest = z.infer<typeof MfaSetupTotpCompleteRequestSchema>;

export const MfaSetupEmailCompleteRequestSchema = z.object({
  session: z.string().min(1, 'session is required'),
  username: z.string().min(1, 'username is required'),
  loginType: LoginTypeSchema,
});
export type MfaSetupEmailCompleteRequest = z.infer<typeof MfaSetupEmailCompleteRequestSchema>;

// ── MFA ──────────────────────────────────────────────────────────────────────

export const MfaSetupMethodSchema = z.enum(['TOTP', 'EMAIL_OTP']);
export type MfaSetupMethod = z.infer<typeof MfaSetupMethodSchema>;

export const MfaStatusResponseSchema = z.object({
  mfaEnabled: z.boolean(),
  mfaMethod: MfaSetupMethodSchema.nullable(),
  isSSO: z.boolean(),
  setupRequired: z.boolean(),
});
export type MfaStatusResponse = z.infer<typeof MfaStatusResponseSchema>;

export const MfaMethodsResponseSchema = z.object({
  methods: z.array(MfaSetupMethodSchema),
});
export type MfaMethodsResponse = z.infer<typeof MfaMethodsResponseSchema>;

export const MfaTotpInitiateResponseSchema = z.object({
  otpauthUri: z.string(),
  qrCodeDataUrl: z.string(),
});
export type MfaTotpInitiateResponse = z.infer<typeof MfaTotpInitiateResponseSchema>;

export const MfaVerifyRequestSchema = z.object({
  code: z.string().regex(/^\d{6}$/, 'code must be exactly 6 digits'),
  loginType: LoginTypeSchema.optional(),
});
export type MfaVerifyRequest = z.infer<typeof MfaVerifyRequestSchema>;

export const MfaSetupCompleteResponseSchema = z.object({
  mfaMethod: MfaSetupMethodSchema,
});
export type MfaSetupCompleteResponse = z.infer<typeof MfaSetupCompleteResponseSchema>;

export const MfaEmailInitiateResponseSchema = z.object({
  maskedEmail: z.string(),
});
export type MfaEmailInitiateResponse = z.infer<typeof MfaEmailInitiateResponseSchema>;

export const MfaResendEmailOtpRequestSchema = z.object({
  session: z.string().min(1, 'session is required'),
  username: z.string().min(1, 'username is required'),
});
export type MfaResendEmailOtpRequest = z.infer<typeof MfaResendEmailOtpRequestSchema>;

export const MfaResendEmailOtpResponseSchema = z.object({
  session: z.string(),
  maskedEmail: z.string(),
});
export type MfaResendEmailOtpResponse = z.infer<typeof MfaResendEmailOtpResponseSchema>;

// ── MFA admin reset ───────────────────────────────────────────────────────────

export const MfaAdminResetRequestSchema = z.object({
  cognitoSub:  z.string().min(1, 'cognitoSub is required'),
  reason:      z.string().min(3, 'reason must be at least 3 characters').max(500),
  loginType:   z.enum(['donor']).optional(),
});
export type MfaAdminResetRequest = z.infer<typeof MfaAdminResetRequestSchema>;

export const MfaAdminResetResponseSchema = z.object({
  message:    z.string(),
  cognitoSub: z.string(),
});
export type MfaAdminResetResponse = z.infer<typeof MfaAdminResetResponseSchema>;

export const MfaPreferenceChangeRequestSchema = z.object({
  preferredMethod: MfaSetupMethodSchema,
});
export type MfaPreferenceChangeRequest = z.infer<typeof MfaPreferenceChangeRequestSchema>;

/**
 * Response after successfully responding to a challenge — always returns tokens.
 */
export const RespondToChallengeResponseSchema = z.object({
  id_token: z.string(),
  access_token: z.string(),
  refresh_token: z.string().optional(),
  token_type: z.string(),
  expires_in: z.number(),
  user: CognitoLoginUserSchema,
  mfaSetupRequired: z.boolean().optional(),
});
export type RespondToChallengeResponse = z.infer<typeof RespondToChallengeResponseSchema>;

export const LogoutResponseSchema = z.object({
  message: z.string(),
});
export type LogoutResponse = z.infer<typeof LogoutResponseSchema>;

export const JwtPayloadSchema = z.object({
  sub: z.string().uuid(),
  email: z.string().email(),
  role: UserRoleSchema,
  iat: z.number(),
  exp: z.number(),
});
export type JwtPayload = z.infer<typeof JwtPayloadSchema>;

export const PermissionSchema = z.enum([
  'users:read',
  'users:write',
  'users:delete',
  'admin:access',
  'reports:read',
  'reports:write',
]);
export type Permission = z.infer<typeof PermissionSchema>;

export const RolePermissionsSchema = z.record(UserRoleSchema, z.array(PermissionSchema));
export type RolePermissions = z.infer<typeof RolePermissionsSchema>;

// Default role-permission mappings
export const DEFAULT_ROLE_PERMISSIONS: Record<string, Permission[]> = {
  admin: ['users:read', 'users:write', 'users:delete', 'admin:access', 'reports:read', 'reports:write'],
  user: ['users:read', 'reports:read'],
  viewer: ['reports:read'],
};

// ── SSO / OAuth2 Callback ────────────────────────────────────────────────────

/** Query parameters received on the OAuth2 authorization-code callback. */
export const AuthCallbackQuerySchema = z.object({
  code: z.string().min(1, 'Authorization code is required'),
  code_verifier: z.string().min(1, 'PKCE code_verifier is required'),
  state: z.string().optional(),
});
export type AuthCallbackQuery = z.infer<typeof AuthCallbackQuerySchema>;

/** Response returned by the backend after exchanging the authorization code. */
export const AuthCallbackResponseSchema = z.object({
  id_token: z.string(),
  access_token: z.string(),
  refresh_token: z.string().optional(),
  token_type: z.string(),
  expires_in: z.number(),
  user: CognitoLoginUserSchema,
});
export type AuthCallbackResponse = z.infer<typeof AuthCallbackResponseSchema>;

// ── SSO Discovery ────────────────────────────────────────────────────────────

export const SsoProviderSummarySchema = z.object({
  provider_name: z.string(),
  provider_type: z.enum(['SAML', 'OIDC']),
});
export type SsoProviderSummary = z.infer<typeof SsoProviderSummarySchema>;

export const SsoConfigResponseSchema = z.object({
  sso_enabled: z.boolean(),
  idp_type: IdpTypeEnum,
  providers: z.array(SsoProviderSummarySchema),
  authorize_url: z.string().optional(),
});
export type SsoConfigResponse = z.infer<typeof SsoConfigResponseSchema>;

export const TotpInitiateResponseSchema = z.object({
  qrCodeDataUrl: z.string(),
  otpauthUri: z.string(),
});
export type TotpInitiateResponse = z.infer<typeof TotpInitiateResponseSchema>;

export const MfaStatusSchema = z.object({
  mfaEnabled: z.boolean(),
  mfaMethod: z.enum(['TOTP', 'EMAIL_OTP']).nullable(),
  isSSO: z.boolean(),
  setupRequired: z.boolean(),
});
export type MfaStatus = z.infer<typeof MfaStatusSchema>;
